import * as React from 'react';

import {
    AssertionDescriptorDisplayValueTypeEnum,
    AssertionDescriptorUsageTypesEnum,
    AssertionDescriptorValueDisplayTypes,
    AssertionDescriptorValueSourceTypeEnum,
} from '../../utilities/Constants';
import {
    IAssertionDescriptorModel,
    IEntityAssertionModel,
} from '../../interfaces/IAssertionDescriptor';

import CaseFileCheckmateSelectHelper from '../../utilities/CaseFileCheckmateSelectHelper';
import CheckmateSelect from './CheckmateSelect';
import CheckmateSelectHelper from '../../utilities/CheckmateSelectHelper';
import Common from '../../stores/Common';
import { cloneDeep } from 'lodash';

interface IEntityAssertionControlProps {
    additionalIdentifierKey?: any;
    assertionDescriptor?: IAssertionDescriptorModel;
    assertionDescriptors?: IAssertionDescriptorModel[];
    assertionDescriptorUsageType: AssertionDescriptorUsageTypesEnum;
    editMode?: boolean;
    entityAssertions: IEntityAssertionModel[] | undefined;
    handleNarrativeFieldEdit?: (entityAssertion: IEntityAssertionModel) => void;
    narrativeFieldAsPopup?: boolean;
    refreshChange?: (entityAssertions: IEntityAssertionModel[], key?: any) => void;
}

interface IEntityAssertionControlState {
    entityAssertions?: IEntityAssertionModel[];
    showNarrativeModal?: boolean;
}
export class EntityAssertionControl extends React.Component<
    IEntityAssertionControlProps,
    IEntityAssertionControlState
> {
    constructor(props: IEntityAssertionControlProps) {
        super(props);
        this.state = {
            entityAssertions: [],
        };

        this.onSearchableSingleSelectChange = this.onSearchableSingleSelectChange.bind(this);
    }

    componentDidMount() {
        this.setState({
            entityAssertions: this.props.entityAssertions,
        });
    }

    componentDidUpdate(prevProps: IEntityAssertionControlProps) {
        if (prevProps.entityAssertions != this.props.entityAssertions) {
            this.setState({
                entityAssertions: this.props.entityAssertions,
            });
        }
    }

    getValue(assertion: IEntityAssertionModel, editMode?: boolean) {
        if (!assertion || !assertion.assertionDescriptorGuid) return '';

        let returnValue = '';

        if (!this.props.assertionDescriptor) {
            return null;
        }

        const valueSourceTypeId = this.props.assertionDescriptor.valueSourceType!.id;
        const displayTypeId = this.props.assertionDescriptor.valueDisplayType?.id ?? 0;

        const foundADV = this.props.assertionDescriptor.assertionDescriptorValues?.find(
            (adv) => adv.guid === assertion.assertionDescriptorValueGuid
        );

        switch (valueSourceTypeId) {
            case AssertionDescriptorValueSourceTypeEnum.Selection.Value: {
                returnValue = assertion.assertionDescriptorValueGuid
                    ? editMode
                        ? assertion.assertionDescriptorValueGuid!
                        : foundADV?.text ?? ''
                    : '';
                break;
            }
            case AssertionDescriptorValueSourceTypeEnum.Boolean.Value:
                if (assertion.booleanValue != undefined)
                    returnValue = editMode
                        ? assertion.booleanValue
                            ? 'true'
                            : 'false'
                        : assertion.booleanValue
                        ? 'Yes'
                        : 'No';
                break;
            case AssertionDescriptorValueSourceTypeEnum.Text.Value:
                switch (displayTypeId) {
                    case AssertionDescriptorDisplayValueTypeEnum.Currency.Value:
                        if (assertion.numericValue)
                            returnValue = editMode
                                ? assertion.numericValue.toString()
                                : Common.formatCurrency(assertion.numericValue.toString())!;
                        break;
                     case AssertionDescriptorDisplayValueTypeEnum.CurrencyWithCents.Value:
                        if (assertion.numericValue)
                            returnValue = editMode
                                ? assertion.numericValue.toString()
                                : Common.formatCurrencyWithCents(assertion.numericValue.toString())!;
                        break;
                    case AssertionDescriptorDisplayValueTypeEnum.DateTime.Value:
                        if (assertion.dateTimeValue)
                            returnValue = Common.dateFormat(assertion.dateTimeValue.toString())!;
                        break;
                    case AssertionDescriptorDisplayValueTypeEnum.Percentage.Value:
                        if (assertion.numericValue)
                            returnValue =
                                parseInt(assertion.numericValue.toString()).toString() + editMode
                                    ? ''
                                    : '%';
                        break;
                    case AssertionDescriptorDisplayValueTypeEnum.Number.Value:
                        if (assertion.numericValue)
                            returnValue = parseInt(assertion.numericValue.toString()).toString();
                        break;
                    case AssertionDescriptorDisplayValueTypeEnum.Decimal.Value:
                        if (assertion.numericValue) returnValue = assertion.numericValue.toString();
                        break;
                    default:
                        returnValue = assertion.textValue || '';
                        break;
                }
                break;
            default:
                break;
        }

        return returnValue;
    }

    onSearchableSingleSelectChange(selectedItem?: any) {
        const entityAssertions = cloneDeep(this.state.entityAssertions ?? []);

        if (entityAssertions?.length > 0) {
            const firstCaseAssertion = entityAssertions.at(0);

            const valueSourceTypeId = this.props.assertionDescriptor?.valueSourceType?.id ?? 0;

            if (!firstCaseAssertion) {
                return;
            }
            if (selectedItem) {
                switch (valueSourceTypeId) {
                    case AssertionDescriptorValueSourceTypeEnum.Boolean.Value:
                        firstCaseAssertion.booleanValue = selectedItem.id === 1;
                        break;
                    case AssertionDescriptorValueSourceTypeEnum.Selection.Value:
                        firstCaseAssertion.assertionDescriptorValueGuid = selectedItem.guid;
                        break;
                    default:
                        break;
                }
            } else {
                firstCaseAssertion.booleanValue = undefined;
                firstCaseAssertion.assertionDescriptorValueGuid = undefined;
            }

            this.setState({ entityAssertions });
            this.props.refreshChange?.(entityAssertions, this.props.additionalIdentifierKey);
        }
    }

    getSingleValueEditControl = () => {
        if (!this.state.entityAssertions || this.state.entityAssertions.length !== 1) {
            return;
        }

        const assertion = this.state.entityAssertions?.at(0);

        if (!assertion) {
            return;
        }

        const valueSourceTypeId = this.props.assertionDescriptor?.valueSourceType?.id;
        const displayTypeId = this.props.assertionDescriptor?.valueDisplayType?.id ?? 0;

        switch (valueSourceTypeId) {
            case AssertionDescriptorValueSourceTypeEnum.Selection.Value: {
                const options = CaseFileCheckmateSelectHelper.getOptionsForCheckmateSelect(
                    this.props.assertionDescriptor?.assertionDescriptorValues ?? []
                );

                return (
                    <span>
                        <CheckmateSelect
                            options={options}
                            name={assertion.assertionDescriptorGuid!}
                            value={CaseFileCheckmateSelectHelper.getCheckmateSelectValue(
                                assertion!,
                                this.props.assertionDescriptors ?? []
                            )}
                            onChange={this.onSearchableSingleSelectChange}
                        />
                    </span>
                );
                break;
            }
            case AssertionDescriptorValueSourceTypeEnum.Boolean.Value:
                return (
                    <span>
                        <CheckmateSelect
                            options={CheckmateSelectHelper.getBooleanOptions()}
                            name={assertion.assertionDescriptorGuid!}
                            value={CheckmateSelectHelper.getBooleanOptionValue(
                                assertion.booleanValue
                            )}
                            onChange={this.onSearchableSingleSelectChange}
                        />
                    </span>
                );
            case AssertionDescriptorValueSourceTypeEnum.Text.Value:
                switch (displayTypeId) {
                    case AssertionDescriptorDisplayValueTypeEnum.Currency.Value:
                    case AssertionDescriptorDisplayValueTypeEnum.CurrencyWithCents.Value:
                        return (
                            <span style={{ marginLeft: '10px' }}>
                                <span>$</span>
                                <input
                                    className="form-control"
                                    type="number"
                                    value={assertion.numericValue}
                                    onChange={this.handleChange}
                                />
                            </span>
                        );
                    case AssertionDescriptorDisplayValueTypeEnum.DateTime.Value:
                        return (
                            <input
                                type="date"
                                className={
                                    'form-control' +
                                    (assertion.dateTimeValue &&
                                    Common.isValidDate(assertion.dateTimeValue)
                                        ? ''
                                        : ' unselectClass')
                                }
                                value={Common.safeDateFormat(assertion.dateTimeValue)}
                                onChange={this.handleChange}
                            />
                        );
                    case AssertionDescriptorDisplayValueTypeEnum.Percentage.Value:
                        return (
                            <span>
                                <span>%</span>
                                <input
                                    className="form-control"
                                    type="number"
                                    name={assertion.assertionDescriptorGuid}
                                    value={assertion.numericValue}
                                    onChange={this.handleChange}
                                />
                            </span>
                        );
                    case AssertionDescriptorDisplayValueTypeEnum.Number.Value:
                        break;
                    case AssertionDescriptorDisplayValueTypeEnum.Decimal.Value:
                        break;
                    case AssertionDescriptorDisplayValueTypeEnum.ShortText.Value:
                        return (
                            <input
                                className="form-control"
                                type="text"
                                maxLength={200}
                                name={assertion.assertionDescriptorGuid}
                                value={assertion.textValue ?? ''}
                                onChange={this.handleChange}
                            />
                        );
                    case AssertionDescriptorDisplayValueTypeEnum.Narrative.Value:
                        if (this.props.narrativeFieldAsPopup && this.props.handleNarrativeFieldEdit)
                            return (
                                <button
                                    className="btn btn-no-bg"
                                    onClick={() => this.props.handleNarrativeFieldEdit!(assertion)}
                                    title={assertion.textValue}
                                >
                                    <i
                                        className={
                                            assertion.textValue
                                                ? 'fal fa-pen color-black'
                                                : 'fal fa-pen color-gray'
                                        }
                                    />
                                </button>
                            );
                        else {
                            return (
                                <textarea
                                    className="form-control"
                                    name={assertion.assertionDescriptorGuid}
                                    rows={3}
                                    value={assertion.textValue}
                                    onChange={this.handleChange}
                                />
                            );
                        }
                    case AssertionDescriptorDisplayValueTypeEnum.SSN.Value:
                        return (
                            <input
                                className="form-control"
                                type="text"
                                placeholder="XXX-XX-XXXX"
                                maxLength={11}
                                name={assertion.assertionDescriptorGuid}
                                value={assertion.textValue}
                                onChange={this.handleChange}
                            />
                        );
                    default:
                        break;
                }
                break;
            default:
                break;
        }
    };

    handleChange = (
        e:
            | React.ChangeEvent<HTMLInputElement>
            | React.ChangeEvent<HTMLSelectElement>
            | React.ChangeEvent<HTMLTextAreaElement>
    ) => {
        const valueSourceTypeId = this.props.assertionDescriptor?.valueSourceType!.id;
        const valueDisplayTypeId = this.props.assertionDescriptor?.valueDisplayType?.id ?? 0;

        const multiValue =
            valueDisplayTypeId == AssertionDescriptorValueDisplayTypes.MultiSelect.Value;

        const entityAssertions = cloneDeep(this.state.entityAssertions);

        if (!entityAssertions) {
            return;
        }

        if (!multiValue && entityAssertions.length > 0) {
            const entityAssertion = entityAssertions[0];
            const value = e.target.value;

            switch (valueSourceTypeId) {
                case AssertionDescriptorValueSourceTypeEnum.Selection.Value:
                    entityAssertion.assertionDescriptorValueGuid = value;
                    break;
                case AssertionDescriptorValueSourceTypeEnum.Boolean.Value:
                    if (value) {
                        entityAssertion.booleanValue = value === 'true';
                    } else {
                        entityAssertion.booleanValue = undefined;
                    }
                    break;
                case AssertionDescriptorValueSourceTypeEnum.Text.Value:
                    switch (valueDisplayTypeId) {
                        case AssertionDescriptorValueDisplayTypes.Currency.Value:
                        case AssertionDescriptorValueDisplayTypes.CurrencyWithCents.Value:
                        case AssertionDescriptorValueDisplayTypes.Percentage.Value:
                        case AssertionDescriptorValueDisplayTypes.Decimal.Value:
                        case AssertionDescriptorValueDisplayTypes.Number.Value:
                            entityAssertion.numericValue = parseFloat(value);
                            break;
                        case AssertionDescriptorValueDisplayTypes.DateTime.Value:
                            if (value && Common.isValidDate(value)) {
                                entityAssertion.dateTimeValue = Common.dateFormat(value);
                            } else {
                                entityAssertion.dateTimeValue = undefined;
                            }
                            break;
                        default:
                            entityAssertion.textValue = value;
                            break;
                    }
                    break;
                default:
                    break;
            }
        }

        this.setState({ entityAssertions });
        this.props.refreshChange?.(entityAssertions, this.props.additionalIdentifierKey);
    };

    render() {
        if (this.props.editMode && (this.state.entityAssertions?.length ?? 0) > 0) {
            return <div>{this.getSingleValueEditControl()}</div>;
        }

        const firstAssertion = this.state.entityAssertions?.at(0);
        if (firstAssertion) {
            return (
                <div>
                    {this.getValue(firstAssertion)
                        ?.split('\n')
                        .map((item, key: number) => {
                            return (
                                <span key={key}>
                                    {item}
                                    <br />
                                </span>
                            );
                        })}
                </div>
            );
        }

        return null;
    }
}

export default EntityAssertionControl;
